/**
 * Generates the base URL for Cloudinary image storage using environment variables.
 *
 * This function retrieves the Cloudinary cloud name from the environment variables
 * and constructs the base URL for fetching images from Cloudinary. It supports an optional
 * `variantPrefix`, which, if provided, is added as a transformation in the URL.
 *
 * @param {Object} options - Optional parameters for URL customization.
 * @param {string} [options.variantPrefix] - The Cloudinary transformation variant.
 * @returns {string} The Cloudinary base URL for image storage, optionally including the transformation variant.
 */
export const getCloudinaryBaseUrl = (options = {}) => {
  const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;

  // If cloudName is missing, log a warning and return an empty string
  if (!cloudName) {
    console.warn('Cloudinary environment variables are missing!');
    return '';
  }

  const { variantPrefix } = options;

  const transformation =
    typeof variantPrefix === 'string' && variantPrefix.trim() ? `/t_${variantPrefix}` : '';

  // No version segment: Cloudinary already serves the current asset from
  // /upload/, with a 30-day CDN cache. This previously appended `v${Date.now()}`,
  // which made every URL unique on every render — defeating the browser and CDN
  // cache entirely, and (because SSR and hydration render at different
  // milliseconds) producing a src mismatch on every <img>, so React rebuilt each
  // image node on hydration and the whole page visibly flashed.
  // To bust the cache after re-uploading an image, upload with `invalidate: true`.
  return `https://res.cloudinary.com/${cloudName}/image/upload${transformation}`;
};
